<script setup>
import {computed, onMounted, ref, watch} from "vue";
import {usePiniaStore} from "../../store/pinia_store";
import _, {isEmpty, isNumber} from "lodash";
import GiftBar from "./GiftBar.vue";
import VoucherCodeWrapper from "./VoucherCodeWrapper.vue";
import VoucherLines from "./VoucherLines.vue";

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  dentaidApiInstance: {
    type: Function,
    required: true
  },
  cartEventBus: {
    type: Object,
    required: true,
  },
  gifts: {
    type: Array,
    required: false,
  },
  eventBus: {
    type: Object,
    required: true,
  }
})

const piniaStore = usePiniaStore();

const loadingText = ref(window.Drupal.t('Loading...'));
const noProductsText = ref(window.Drupal.t('No products in cart'));
const continueOrderText = ref(window.Drupal.t('Continue ordering'));
const freeShippingText = ref(window.Drupal.t('Free shipping from € 100, - excl VAT.'));
const shippingCostText = ref(window.Drupal.t('Shipping cost'));
const totalText = ref(window.Drupal.t('Total (excl VAT.)'));
const discountText = ref(window.Drupal.t('Discount'));
const subTotalText = ref(window.Drupal.t('Subtotal'));
const productsText = ref(window.Drupal.t('Products'));
const itemsText = ref(window.Drupal.t('items'));
const orderOverviewText = ref(window.Drupal.t('Order overview'));
const continueShoppingText = ref(window.Drupal.t('Continue shopping'));
const chosenGiftText = ref(window.Drupal.t('Chosen gift:'));
const minOrderAMountText = ref(window.Drupal.t('Minimum order amount is'));
const selectedGift = ref(null);
const loading = ref(true);
const vouchers = ref({});
const productsOpen = ref(false);
const discountsOpen = ref(false);

const minimumOrderAmountReached = computed(() => {
  return piniaStore.cart.total >= piniaStore.cart.conditions.min_order_value;
})

const hasCartLines = computed(() => {
  if (lodash.isNil(piniaStore.cart)) {
    return false;
  }

  return !_.isEmpty(piniaStore.cart.lines);
})

const totalQuantity = computed(() => {
  if (!hasCartLines.value) {
    return '';
  }

  let quantity = 0;

  _.forEach(piniaStore.cart.lines, (line) => {
    quantity = quantity + line.quantity
  })

  return quantity;
});

const hasGift = computed(() => {
  return !lodash.isNil(selectedGift.value)
});

const productOverviewUrl = computed(() => {
  return `/node/23`;
});

const hasDiscount = computed(() => {
  return piniaStore.cart.discount !== 0;
});

const cartLines = computed(() => {
  return Object.assign({}, _.filter(piniaStore.cart.lines, function (cartLine) {
    return cartLine.type === 'Product';
  }));
});

const getVouchers = (lines) => {
  return Object.assign({}, _.filter(lines, function (cartLine) {
    return cartLine.type === 'Voucher';
  }));
}

const linesArray = computed(() => Object.values(piniaStore.cart?.lines ?? {}));
const discountsCount = computed(() =>
  linesArray.value.reduce((acc, l) => {
    if (l.type !== 'Product') return acc;

    const tup = Number(l.total_unit_price ?? 0);
    const tnp = Number(l.total_net_price ?? 0);

    return acc + (Number.isFinite(tup) && Number.isFinite(tnp) && tup - tnp > 0 ? 1 : 0);
  }, 0)
);

const refreshCart = (data) => {
  piniaStore.cart = data;
}

const handleGiftChanged = (payload) => {
  selectedGift.value = payload;
}

const currencyFormatter = (value) => {
  if (isNumber((value)) && value !== 0) {
    value = value / 100;
  }

  return new Intl.NumberFormat('nl-NL', {style: 'currency', currency: 'EUR'}).format(value)
}

const formatPriceForDataLayer = (priceInCents) => {
  return (priceInCents / 100).toFixed(2);
}

const calculateDiscountPercentage = (product) => {
  const totalUnitPrice = product.total_unit_price || 0;
  const totalNetPrice = product.total_net_price || 0;

  if (totalUnitPrice === 0) {
    return 0;
  }

  const discountPercentage = ((totalUnitPrice - totalNetPrice) / totalUnitPrice) * 100;
  return parseFloat(discountPercentage.toFixed(4));
}

const calculateUnitDiscount = (product) => {
  const totalUnitPrice = product.total_unit_price || 0;
  const totalNetPrice = product.total_net_price || 0;
  const quantity = product.quantity || 1;
  const discountPercentage = calculateDiscountPercentage(product);
  const discountPerUnit = (totalUnitPrice - totalNetPrice) / quantity;
  return formatPriceForDataLayer(discountPerUnit);
}

const calculateTotalProductDiscount = (product) => {
  const totalUnitPrice = product.total_unit_price || 0;
  const totalNetPrice = product.total_net_price || 0;
  const totalDiscount = totalUnitPrice - totalNetPrice;
  return formatPriceForDataLayer(totalDiscount);
}

const getAppliedVoucherCodes = () => {
  const voucherLines = _.filter(piniaStore.cart.lines, (line) => line.type === 'Voucher');
  const voucherCodes = _.map(voucherLines, (voucher) => voucher.code || voucher.name);
  return voucherCodes.join(', ');
}

const mappedProductsForViewCart = () => {
  if (!hasCartLines.value) {
    return []
  }

  const appliedCoupons = getAppliedVoucherCodes();

  return _.map(cartLines.value, (product, index) => {
    const brand = product.translation?.extra_attributes?.brand || '';
    const totalProductPrice = formatPriceForDataLayer(product.unit_price * (product.quantity || 1));
    const totalProductDiscount = calculateTotalProductDiscount(product);
    return {
      item_id: product.sku || '',
      item_name: (product.dwh_product?.name ?? product.translation?.name)?.toLowerCase() || '',
      affiliation: brand,
      coupon: appliedCoupons,
      discount: calculateUnitDiscount(product),
      index: index,
      item_brand: brand,
      item_category: product.translation?.extra_attributes?.category || '',
      ...(piniaStore.cart.id && { item_list_id: String(piniaStore.cart.id) }),
      item_variant: product.translation?.extra_attributes?.volume || '',
      price: formatPriceForDataLayer(product.unit_price),
      quantity: product.quantity || 1,
      total_product_price: (parseFloat(totalProductPrice) - parseFloat(totalProductDiscount)).toFixed(2),
      total_product_discount: totalProductDiscount,
    }
  });
}

const pushCartOverviewToDataLayer = () => {
  if (!hasCartLines.value) {
    return;
  }

  window.dataLayer.push({ecommerce: null});

  const appliedCoupons = getAppliedVoucherCodes();

  window.dataLayer.push({
    event: 'view_cart',
    ecommerce: {
      currency: 'EUR',
      value: formatPriceForDataLayer(piniaStore.cart.total),
      discount: formatPriceForDataLayer(piniaStore.cart.discount),
      ...(appliedCoupons && { coupon: appliedCoupons }),
      items: mappedProductsForViewCart(),
    }
  });
}

const checkInitialGift = () => {
  let gift = _.find(piniaStore.cart.lines, (cartLine) => {
    return cartLine.type === 'Gift';
  });

  if (lodash.isNil(gift)) {
    selectedGift.value = null;

    return;
  }

  selectedGift.value = _.find(piniaStore.gifts.lines, (giftLine) => {
    return giftLine.sku === gift.sku
  });
}

watch(() => piniaStore.isLoading, (isLoading) => {
  vouchers.value = getVouchers(piniaStore.cart.lines);

  if (hasGift) {
    checkInitialGift();
  }

  // Push view_cart event when cart data is loaded
  if (!isLoading && hasCartLines.value) {
    pushCartOverviewToDataLayer();
  }

}, {deep: true})

watch(() => piniaStore.cart.total, () => {
  setTimeout(() => {
    checkInitialGift();
  }, 250)
}, {deep: true})

onMounted(() => {
  props.eventBus.$on('refresh-cart', (data) => refreshCart(data));
  props.eventBus.$on('do-refresh-cart', (lines) => {
    vouchers.value = getVouchers(lines);
  });
  props.eventBus.$on('get-cart-completed', () => pushCartOverviewToDataLayer());

  loading.value = false;
})

</script>

<template>
  <div class="vue-cart-components-wrapper">
    <div class="container">
      <div class="row">
        <div class="col-12">
          <h1>{{ title }}</h1>
        </div>
      </div>

      <div class="row cart-overview" v-if="hasCartLines && !loading">
        <div class="col-lg-8 cart-lines">
          <gift-bar
            :dentaid-api-instance="dentaidApiInstance"
            :min-order-value="piniaStore.gifts.min_order_value"
            :has-cart-lines="hasCartLines"
            :cart-total="piniaStore.cart.total"
            :jwt-token="piniaStore.parsedToken"
            :event-bus="eventBus"
            :language="piniaStore.language"
            :initial-gift="piniaStore.cart.gift"
            @gift-changed="handleGiftChanged"
          ></gift-bar>

          <dentaid-cart-table
            :items="cartLines"
            :event-bus="eventBus"
            :jwt-token="piniaStore.parsedToken"
            :dentaid-api-instance="dentaidApiInstance"
            :cart-event-bus="cartEventBus"
          ></dentaid-cart-table>

          <a :href="productOverviewUrl" class="go-back"><span class="icon-arrow-right"></span>
            {{ continueShoppingText }}</a>
        </div>

        <div class="col-xl-3 offset-xl-1 col-lg-4 order-overview">
          <div class="totals-wrapper">
            <h3>{{ orderOverviewText }}</h3>

            <voucher-code-wrapper
              v-if="!piniaStore.isLoading"
              :event-bus="eventBus"
              :dentaid-api-instance="dentaidApiInstance"
              :jwt-token="piniaStore.parsedToken"
              :language="piniaStore.language"
              :initial-vouchers="vouchers"
            ></voucher-code-wrapper>

            <div class="vue-voucher-code-wrapper row">
              <div class="col-12">
                <button class="toggle-button products w-100" :class="{'active': productsOpen}" @click="productsOpen = !productsOpen">
                  <span class="toggle-text">
                    {{ productsText }}
                    <span class="toggle-text__counter">({{ totalQuantity }})</span>
                  </span>
                  <span class="price ms-auto">{{ currencyFormatter(piniaStore.cart.total_unit_price) }}</span>
                </button>
              </div>

              <div class="col-12" v-show-slide="productsOpen">
                <div class="accordion-line" v-for="line in cartLines" :key="line.sku">
                  <span class="product-name">{{ line.translation.name }}</span>
                  <span class="price">{{ currencyFormatter(line.total_unit_price) }}</span>
                </div>
              </div>
            </div>

            <voucher-lines
              v-if="!isEmpty(vouchers)"
              :voucher-lines="vouchers"
            ></voucher-lines>

            <div class="vue-voucher-code-wrapper row" v-if="hasDiscount">
              <div class="col-12">
                <button class="toggle-button discount w-100" :class="{'active': discountsOpen}" @click="discountsOpen = !discountsOpen">
                  <span class="toggle-text">
                    {{ discountText }}
                    <span class="toggle-text__counter">({{ discountsCount }})</span>
                  </span>
                  <span class="price ms-auto">- {{ currencyFormatter(piniaStore.cart.discount) }}</span>
                </button>
              </div>

              <div class="col-12" v-show-slide="discountsOpen">
                <template v-for="line in cartLines">
                  <div class="accordion-line" v-if="line.total_unit_price - line.total_net_price > 0" :key="line.sku">
                    <span class="product-name">{{ line.translation.name }}</span>
                    <span class="price">- {{ currencyFormatter(line.total_unit_price - line.total_net_price) }}</span>
                  </div>
                </template>
              </div>
            </div>

            <div class="discount mt-3" v-if="hasGift">
              {{ chosenGiftText }} {{ selectedGift.name }}
            </div>

            <div class="subtotal">
              {{ subTotalText }} ({{ totalQuantity }} {{ itemsText }})
              <span class="price">{{ currencyFormatter(piniaStore.cart.total_net_price) }}</span>
            </div>

            <div class="shipping-cost">
              <template v-if="!piniaStore.customer.is_pharma"> {{ shippingCostText }}</template>
              <span class="price">{{ currencyFormatter(piniaStore.cart.shipping_costs) }}</span>
            </div>

            <div class="shipping-info">
              {{ freeShippingText }}
            </div>

            <div class="total">
              {{ totalText }} <span class="price">{{ currencyFormatter(piniaStore.cart.total) }}</span>
            </div>
          </div>

          <div class="text-center">
            <a :href="`/checkout/confirm`" class="btn-primary" v-if="minimumOrderAmountReached">{{
                continueOrderText
              }}</a>

            <p v-else class="text-danger font-weight-bold mt-3">{{ minOrderAMountText }}
              {{ currencyFormatter(piniaStore.cart.conditions.min_order_value) }}</p>
          </div>

        </div>
      </div>

      <template v-if="!hasCartLines && !loading">
        <div class="row no-products-row">
          <div class="col-12">
            <p class="virtual-h2">{{ noProductsText }}</p>
          </div>
        </div>
      </template>

      <template v-if="loading">
        <div class="row loading-row">
          <div class="col-12">
            <p class="virtual-h2">{{ loadingText }}
              <dentaid-loader></dentaid-loader>
            </p>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
